import { useMutation } from "@apollo/client";
import {
  FormControl,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { MAX_BLOCKED_NOTE_LENGTH } from "encamp-shared/src/waste/constants";
import { gql } from "generated-graphql";
import { ManifestBlockReason } from "generated-graphql/graphql";
import { useState } from "react";

import {
  MANIFEST_BLOCK_REASON_GROUPS,
  MANIFEST_BLOCK_REASON_LABELS,
} from "./manifestBlockReasonDisplay";

export const BLOCK_PROCESSED_MANIFEST = gql(`
  mutation BlockProcessedManifest($processedManifestId: ID!, $reason: ManifestBlockReason!, $note: String) {
    blockProcessedManifest(processedManifestId: $processedManifestId, reason: $reason, note: $note) {
      processedManifestId
      blockedReason
      blockedNote
      blockedAt
      blockedBy {
        id
        email
        person {
          first
          last
        }
      }
    }
  }
`);

type Props = {
  open: boolean;
  processedManifestId: string;
  /** For the subheader, so the reviewer can confirm which manifest they're blocking. */
  manifestTrackingNumber?: string | null;
  /** Pre-fills the note (e.g. text typed into the resolve dialog before routing here). */
  initialNote?: string | null;
  onClose: () => void;
  /** Called after a successful block (the dialog closes itself first). */
  onBlocked?: () => void;
};

const ALL_REASONS: readonly ManifestBlockReason[] = Object.values(ManifestBlockReason);

/**
 * Block a manifest with a reason and optional note; blocking unassigns it and removes
 * it from the actionable queue. Mount only while open (`{open && <MarkBlockedDialog …/>}`)
 * so each opening starts with fresh state.
 */
export function MarkBlockedDialog({
  open,
  processedManifestId,
  manifestTrackingNumber,
  initialNote,
  onClose,
  onBlocked,
}: Props) {
  const [reason, setReason] = useState<ManifestBlockReason | "">("");
  const [note, setNote] = useState(initialNote ?? "");
  const alerts = useAlerts();

  // For Other, the note is the record: no note means nothing was actually recorded.
  const noteRequired = reason === ManifestBlockReason.Other;
  const noteMissing = noteRequired && note.trim() === "";

  const [block, { loading }] = useMutation(BLOCK_PROCESSED_MANIFEST, {
    // Refresh the header block state, the queue grid, and the scoreboard so the
    // manifest visibly leaves the actionable set before the dialog closes.
    refetchQueries: ["ManifestBlockState", "StaffManifestReviewManifests", "ManifestReviewSummary"],
    awaitRefetchQueries: true,
  });

  const handleSubmit = async () => {
    if (!reason) return;
    try {
      await block({
        variables: {
          processedManifestId,
          reason,
          note: note.trim() === "" ? null : note.trim(),
        },
      });
      alerts.success("Manifest marked blocked");
      onClose();
      onBlocked?.();
    } catch (e) {
      alerts.error("Failed to mark the manifest blocked", e instanceof Error ? e : undefined);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      slotProps={{ paper: { sx: { width: 560, maxWidth: "90vw" } } }}
      title="Mark manifest blocked"
      subheader={manifestTrackingNumber ?? undefined}
      primaryActionButtonProps={{
        onClick: handleSubmit,
        loading,
        loadingText: "Marking blocked...",
        hideStartIcon: true,
        saveText: "Mark blocked",
        disabled: loading || !reason || noteMissing,
      }}
      cancelButtonProps={{ disabled: loading }}
    >
      <FormControl fullWidth size="small" sx={{ mt: 1 }}>
        <InputLabel id="mark-blocked-reason-label">Reason</InputLabel>
        <Select
          labelId="mark-blocked-reason-label"
          label="Reason"
          value={reason}
          onChange={(e) => {
            const value = ALL_REASONS.find((r) => r === e.target.value);
            setReason(value ?? "");
          }}
        >
          {MANIFEST_BLOCK_REASON_GROUPS.flatMap((group) => [
            <ListSubheader key={group.label}>{group.label}</ListSubheader>,
            ...group.reasons.map((r) => (
              <MenuItem key={r} value={r}>
                {MANIFEST_BLOCK_REASON_LABELS[r]}
              </MenuItem>
            )),
          ])}
        </Select>
      </FormControl>
      <TextField
        label={noteRequired ? "Note" : "Note (optional)"}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        size="small"
        multiline
        minRows={2}
        fullWidth
        sx={{ mt: 2 }}
        error={noteMissing}
        helperText={
          noteRequired ? "For Other, the note is the record — say what's blocking this." : undefined
        }
        slotProps={{ htmlInput: { maxLength: MAX_BLOCKED_NOTE_LENGTH } }}
      />
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5 }}>
        Blocking unassigns the manifest and removes it from the actionable queue until someone
        unblocks it. Its findings stay workable.
      </Typography>
    </Dialog>
  );
}
